// src/components/fix-actions/optimization-studio-tab.tsx
import {
  useCallback, useEffect, useMemo, useRef, useState,
} from "react";
import {
  ChevronDown, ChevronRight, Sparkles, AlertTriangle,
  Loader2, LayoutGrid, List, ExternalLink,
} from "lucide-react";
import { useOptimizationStudio } from "../../hooks/fix-actions/useOptimizationStudio";
import type { StrategyArtifact } from "../../services/fix-actions/optimization-studio-service";
import { ClaimCheckBadge } from "./artifact-claim-check";

// ─────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────
function formatRelative(ts: string): string {
  if (!ts) return "—";
  const d = new Date(ts);
  if (isNaN(d.getTime())) return "—";
  const diff = Date.now() - d.getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(diff / 3600000);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(diff / 86400000)}d ago`;
}

function statusColors(status: string) {
  const s = (status || "").toLowerCase();
  if (s === "published" || s === "deployed")
    return { bg: "var(--alpha-teal-10)", text: "var(--alpha-teal)", border: "var(--alpha-teal-20)" };
  if (s === "ready" || s === "ready to launch")
    return { bg: "var(--success-bg)", text: "var(--success-text)", border: "var(--success-border)" };
  if (s === "in review")
    return { bg: "var(--warning-bg)", text: "var(--warning-text)", border: "var(--warning-border)" };
  return { bg: "var(--muted)", text: "var(--muted-foreground)", border: "var(--border)" };
}

// ─────────────────────────────────────────────────────────────
// Artifact card
// ─────────────────────────────────────────────────────────────
function ArtifactCard({
  artifact,
  onOpen,
  isHighlighted,
}: {
  artifact: StrategyArtifact;
  onOpen: (a: StrategyArtifact) => void;
  isHighlighted?: boolean;
}) {
  const sc = statusColors(artifact.status);

  return (
    <div
      className="rounded-xl border transition-all cursor-pointer hover:shadow-md"
      style={{
        backgroundColor: "var(--background)",
        borderColor: isHighlighted ? "var(--alpha-teal)" : "var(--border)",
        padding: "16px",
        boxShadow: isHighlighted ? "0 0 0 2px var(--alpha-teal-30)" : undefined,
      }}
      onClick={() => onOpen(artifact)}
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex-1 min-w-0">
          <div
            style={{
              fontSize: "var(--text-sm)",
              fontWeight: "var(--font-weight-semibold)",
              color: "var(--foreground)",
              lineHeight: "1.4",
              marginBottom: "4px",
              overflowWrap: "anywhere",
              wordBreak: "break-word",
            }}
          >
            {artifact.name}
          </div>
          <div style={{ fontSize: "var(--text-xs)", color: "var(--muted-foreground)" }}>
            {artifact.type}
          </div>
        </div>
        <span
          className="rounded px-2 py-0.5 flex-shrink-0"
          style={{
            fontSize: "var(--text-xs)",
            fontWeight: "var(--font-weight-semibold)",
            backgroundColor: sc.bg,
            color: sc.text,
            border: `1px solid ${sc.border}`,
          }}
        >
          {artifact.status}
        </span>
      </div>

      {artifact.intent && (
        <p
          style={{
            fontSize: "var(--text-xs)",
            color: "var(--muted-foreground)",
            lineHeight: "1.5",
            marginBottom: "10px",
            overflow: "hidden",
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflowWrap: "anywhere",
          }}
        >
          {artifact.intent}
        </p>
      )}

      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-3 flex-wrap">
          <ClaimCheckBadge check={artifact.claimCheck} />
          {artifact.lastUpdated && (
            <span style={{ fontSize: "var(--text-xs)", color: "var(--muted-foreground)" }}>
              {formatRelative(artifact.lastUpdated)}
            </span>
          )}
        </div>
        <button
          className="flex items-center gap-1 shrink-0"
          style={{
            fontSize: "var(--text-xs)",
            color: "var(--alpha-teal)",
            fontWeight: "var(--font-weight-semibold)",
            background: "none",
            border: "none",
            cursor: "pointer",
          }}
          onClick={(e) => { e.stopPropagation(); onOpen(artifact); }}
        >
          Open <ExternalLink className="w-3 h-3" />
        </button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Main tab
// ─────────────────────────────────────────────────────────────
interface OptimizationStudioTabProps {
  onOpenArtifactTray: (artifact: StrategyArtifact) => void;
  onExplainWithAI?: (topic: string) => void;
  optimisticArtifacts?: StrategyArtifact[];
  brandId: string;
  highlightArtifactId?: string | null;
  onClearHighlight?: () => void;
}

export function OptimizationStudioTab({
  onOpenArtifactTray,
  optimisticArtifacts = [],
  brandId,
  highlightArtifactId,
  onClearHighlight,
}: OptimizationStudioTabProps) {
  const { data, loading, error } = useOptimizationStudio({ brandId });

  const [viewMode, setViewMode] = useState<"grouped" | "list">("grouped");
  const [searchQuery, setSearchQuery] = useState("");
  const [filterStatus, setFilterStatus] = useState<string>("all");

  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const didAutoExpand = useRef(false);

  const allArtifacts = useMemo(() => {
    const backendItems = data?.artifacts ?? [];
    const optimisticById = new Map(optimisticArtifacts.map((a) => [a.id, a]));

    const merged: StrategyArtifact[] = backendItems.map((backendItem) =>
      optimisticById.has(backendItem.id) ? optimisticById.get(backendItem.id)! : backendItem
    );

    for (const opt of optimisticArtifacts) {
      if (!backendItems.some((b) => b.id === opt.id)) {
        merged.unshift(opt);
      }
    }

    return merged.filter((a) => {
      const matchesSearch =
        !searchQuery ||
        a.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        a.type.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (a.supportingActionTitle || "").toLowerCase().includes(searchQuery.toLowerCase());
      const matchesStatus =
        filterStatus === "all" || a.status.toLowerCase() === filterStatus.toLowerCase();
      return matchesSearch && matchesStatus;
    });
  }, [data, optimisticArtifacts, searchQuery, filterStatus]);

  const groupedArtifacts = useMemo(() => {
    const groups = new Map<string, { label: string; actionId: number; items: StrategyArtifact[] }>();
    for (const a of allArtifacts) {
      const key = String(a.supportingActionId);
      if (!groups.has(key)) {
        groups.set(key, {
          label: a.supportingActionTitle || `Action #${a.supportingActionId}`,
          actionId: a.supportingActionId,
          items: [],
        });
      }
      groups.get(key)!.items.push(a);
    }
    return groups;
  }, [allArtifacts]);

  useEffect(() => {
    if (didAutoExpand.current) return;
    if (groupedArtifacts.size === 0) return;
    didAutoExpand.current = true;
    setExpandedGroups(new Set(Array.from(groupedArtifacts.keys())));
  }, [groupedArtifacts]);

  const toggleGroup = useCallback((groupKey: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(groupKey)) next.delete(groupKey);
      else next.add(groupKey);
      return next;
    });
  }, []);

  const highlightRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!highlightArtifactId) return;
    const timer = setTimeout(() => {
      highlightRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      if (onClearHighlight) setTimeout(onClearHighlight, 1500);
    }, 200);
    return () => clearTimeout(timer);
  }, [highlightArtifactId, onClearHighlight]);

  const availableStatuses = useMemo(() => {
    const s = new Set<string>();
    (data?.artifacts ?? []).forEach((a) => s.add(a.status));
    optimisticArtifacts.forEach((a) => s.add(a.status));
    return Array.from(s).sort();
  }, [data, optimisticArtifacts]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="w-8 h-8 animate-spin" style={{ color: "var(--signal-blue)" }} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center py-24 px-4">
        <div className="text-center max-w-md">
          <AlertTriangle className="w-10 h-10 mx-auto mb-4" style={{ color: "var(--infrared-coral)" }} />
          <p style={{ color: "var(--muted-foreground)" }}>{error.message}</p>
        </div>
      </div>
    );
  }

  if (allArtifacts.length === 0 && optimisticArtifacts.length === 0) {
    return (
      <div className="flex items-center justify-center py-24 px-4">
        <div className="text-center max-w-md">
          <div
            className="w-20 h-20 mx-auto mb-6 rounded-2xl flex items-center justify-center"
            style={{ backgroundColor: "var(--muted)" }}
          >
            <Sparkles className="w-10 h-10" style={{ color: "var(--muted-foreground)" }} />
          </div>
          <h3 style={{ fontSize: "var(--text-xl)", fontWeight: "var(--font-weight-semibold)", marginBottom: "12px" }}>
            No optimization assets yet
          </h3>
          <p style={{ fontSize: "var(--text-sm)", color: "var(--muted-foreground)" }}>
            Generate your first asset from the Suggested Actions tab.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Toolbar — flex-wrap handles narrow widths naturally */}
      <div
        className="px-4 md:px-8 py-3 md:py-4 border-b flex items-center gap-3 flex-wrap"
        style={{ borderColor: "var(--border)", backgroundColor: "var(--background)" }}
      >
        <div className="relative flex-1" style={{ minWidth: "180px", maxWidth: "280px" }}>
          <input
            type="text"
            placeholder="Search assets…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full px-3 py-2 border rounded-md"
            style={{
              backgroundColor: "var(--background)",
              borderColor: "var(--border)",
              fontSize: "var(--text-sm)",
            }}
          />
        </div>

        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="px-3 py-2 border rounded-md"
          style={{
            backgroundColor: "var(--background)",
            borderColor: "var(--border)",
            fontSize: "var(--text-sm)",
            color: "var(--foreground)",
          }}
        >
          <option value="all">All statuses</option>
          {availableStatuses.map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>

        <div className="ml-auto flex items-center gap-2 flex-wrap">
          <span style={{ fontSize: "var(--text-sm)", color: "var(--muted-foreground)" }}>
            {allArtifacts.length} asset{allArtifacts.length !== 1 ? "s" : ""}
          </span>
          <div className="flex items-center border rounded-md overflow-hidden" style={{ borderColor: "var(--border)" }}>
            <button
              onClick={() => setViewMode("grouped")}
              className="px-3 py-1.5 transition-colors"
              style={{
                backgroundColor: viewMode === "grouped" ? "var(--muted)" : "var(--background)",
                color: viewMode === "grouped" ? "var(--foreground)" : "var(--muted-foreground)",
                border: "none",
                cursor: "pointer",
              }}
            >
              <List className="w-4 h-4" />
            </button>
            <button
              onClick={() => setViewMode("list")}
              className="px-3 py-1.5 transition-colors"
              style={{
                backgroundColor: viewMode === "list" ? "var(--muted)" : "var(--background)",
                color: viewMode === "list" ? "var(--foreground)" : "var(--muted-foreground)",
                border: "none",
                cursor: "pointer",
              }}
            >
              <LayoutGrid className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="px-4 md:px-8 py-4 md:py-6">
        {viewMode === "grouped" ? (
          <div className="space-y-4">
            {Array.from(groupedArtifacts.entries()).map(([groupKey, group]) => {
              const isExpanded = expandedGroups.has(groupKey);
              return (
                <div
                  key={groupKey}
                  className="border rounded-xl overflow-hidden"
                  style={{ borderColor: "var(--border)" }}
                >
                  {/* Group header — stacks label block and action chip on mobile */}
                  <button
                    className="w-full flex items-center justify-between gap-3 px-4 md:px-5 py-3 md:py-4 transition-colors hover:bg-muted/40"
                    style={{
                      backgroundColor: "var(--muted)",
                      border: "none",
                      cursor: "pointer",
                      textAlign: "left",
                    }}
                    onClick={() => toggleGroup(groupKey)}
                  >
                    <div className="flex items-center gap-3 min-w-0 flex-1">
                      {isExpanded
                        ? <ChevronDown className="w-4 h-4 flex-shrink-0" style={{ color: "var(--muted-foreground)" }} />
                        : <ChevronRight className="w-4 h-4 flex-shrink-0" style={{ color: "var(--muted-foreground)" }} />
                      }
                      <div className="min-w-0 flex-1">
                        <div style={{
                          fontSize: "var(--text-sm)",
                          fontWeight: "var(--font-weight-semibold)",
                          color: "var(--foreground)",
                          overflowWrap: "anywhere",
                          wordBreak: "break-word",
                        }}>
                          {group.label}
                        </div>
                        <div style={{ fontSize: "var(--text-xs)", color: "var(--muted-foreground)" }}>
                          {group.items.length} asset{group.items.length !== 1 ? "s" : ""}
                        </div>
                      </div>
                    </div>
                    <span
                      className="px-2 py-0.5 rounded text-xs shrink-0 whitespace-nowrap"
                      style={{
                        backgroundColor: "var(--background)",
                        color: "var(--muted-foreground)",
                        border: "1px solid var(--border)",
                      }}
                    >
                      Action #{group.actionId}
                    </span>
                  </button>

                  {isExpanded && (
                    <div
                      className="grid gap-3 md:gap-4 p-3 md:p-5"
                      style={{
                        gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
                        backgroundColor: "var(--background)",
                      }}
                    >
                      {group.items.map((artifact) => (
                        <div
                          key={artifact.id}
                          ref={artifact.id === highlightArtifactId ? highlightRef : undefined}
                        >
                          <ArtifactCard
                            artifact={artifact}
                            onOpen={onOpenArtifactTray}
                            isHighlighted={artifact.id === highlightArtifactId}
                          />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <div
            className="grid gap-3 md:gap-4"
            style={{ gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))" }}
          >
            {allArtifacts.map((artifact) => (
              <div
                key={artifact.id}
                ref={artifact.id === highlightArtifactId ? highlightRef : undefined}
              >
                <ArtifactCard
                  artifact={artifact}
                  onOpen={onOpenArtifactTray}
                  isHighlighted={artifact.id === highlightArtifactId}
                />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}